import { createTheme } from '@mui/material/styles';

export const liquidGlassTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#ffffff',
      light: '#ffffff',
      dark: '#f5f5f5',
      contrastText: '#424242',
    },
    secondary: {
      main: '#f8f9fa',
      light: '#ffffff',
      dark: '#e9ecef',
      contrastText: '#424242',
    },
    background: {
      default: '#ffffff',
      paper: 'rgba(255, 255, 255, 0.9)',
    },
    text: {
      primary: '#212121',
      secondary: '#424242',
    },
    divider: 'rgba(255, 255, 255, 0.2)',
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica Neue", sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h4: {
      fontWeight: 500,
      letterSpacing: '-0.01em',
    },
    h5: {
      fontWeight: 500,
    },
    h6: {
      fontWeight: 500,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 16,
  },
  components: {
    MuiTooltip: {
      defaultProps: {
        arrow: true,
        enterDelay: 250,
        slotProps: {
          popper: {
            modifiers: [
              { name: 'offset', options: { offset: [0, 10] } },
              { name: 'preventOverflow', options: { padding: 8, altBoundary: true, tether: true } },
            ],
          },
        },
      },
      styleOverrides: {
        tooltip: {
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          color: '#424242',
          borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
          fontSize: '14px',
          padding: '8px 12px',
          maxWidth: 320,
          whiteSpace: 'normal',
          wordBreak: 'break-word',
        },
        arrow: {
          color: 'rgba(255, 255, 255, 0.95)',
          '&::before': {
            border: '1px solid rgba(255, 255, 255, 0.2)',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          // CSS custom properties for ripple colors
          '--ripple-color-light': 'rgba(0, 0, 0, 0.1)',
          '--ripple-color-dark': 'rgba(255, 255, 255, 0.1)',
          '--focus-ring-color': '#1F6FEB',
          '--focus-ring-width': '3px',
          '--focus-ring-offset': '2px',
        },
        body: {
          scrollbarColor: "rgba(158, 158, 158, 0.5) #f5f5f5",
          // Global ripple color based on background
          '&[data-theme="light"], &': {
            '--current-ripple-color': 'var(--ripple-color-light)',
          },
          '&[data-theme="dark"]': {
            '--current-ripple-color': 'var(--ripple-color-dark)',
          },
          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            width: 12,
            height: 12,
          },
          "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
            borderRadius: 12,
            backgroundColor: "rgba(158, 158, 158, 0.5)",
            border: "2px solid #f5f5f5",
            minHeight: 24,
          },
          "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(158, 158, 158, 0.7)",
          },
          "&::-webkit-scrollbar-track, & *::-webkit-scrollbar-track": {
            borderRadius: 12,
            backgroundColor: "#f5f5f5",
          },
          // Remove focus indicator globally (including buttons/links)
          'a:focus-visible, button:focus-visible, [role="button"]:focus-visible, .MuiButtonBase-root:focus-visible': {
            outline: 'none !important',
            outlineOffset: 0,
            boxShadow: 'none !important',
          },
          // Remove default blue outline on all text inputs
          'input:focus, input:focus-visible, textarea:focus, textarea:focus-visible, select:focus, select:focus-visible': {
            outline: 'none !important',
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#424242',
          '&.Mui-focused': {
            color: '#1F1F1F',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: '#424242',
          '&.Mui-focused': {
            color: '#1F1F1F',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.05)',
          '&:hover': {
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.06), 0 6px 20px rgba(0, 0, 0, 0.07)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,249,252,0.8) 100%)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.06), 0 8px 24px rgba(0, 0, 0, 0.07)',
          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 9999,
          padding: '12px 32px',
          fontSize: '16px',
          fontWeight: 500,
          textTransform: 'none',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          overflow: 'hidden',
          minHeight: 44,
          '&:focus-visible': {
            outline: 'none !important',
            outlineOffset: 0,
            boxShadow: 'none !important',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: '-100%',
            width: '100%',
            height: '100%',
            background: 'linear-gradient(105deg, rgba(255,255,255,0) 40%, rgba(255,255,255,0.4) 45%, rgba(255,255,255,0) 50%)',
            transition: 'left 0.8s ease',
            pointerEvents: 'none',
          },
          '&:hover::before': {
            left: '100%',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
          border: '1px solid rgba(255, 255, 255, 0.4)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.03), 0 2px 8px rgba(0, 0, 0, 0.04), inset 0 2px 4px rgba(0, 0, 0, 0.02), inset 0 -1px 2px rgba(255, 255, 255, 0.9)',
          color: '#424242',
          '&:hover': {
            background: 'linear-gradient(135deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.85) 100%)',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.05), inset 0 2px 4px rgba(0, 0, 0, 0.02), inset 0 -1px 2px rgba(255, 255, 255, 0.9)',
            borderColor: 'rgba(255, 255, 255, 0.6)',
          },
          '&:active': {
            transform: 'translateY(0)',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.02), 0 1px 3px rgba(0, 0, 0, 0.04), inset 0 2px 4px rgba(0, 0, 0, 0.02), inset 0 -1px 2px rgba(255, 255, 255, 0.9)',
          },
        },
        outlined: {
          borderColor: 'rgba(255, 255, 255, 0.4)',
          background: 'rgba(255, 255, 255, 0.5)',
          '&:hover': {
            borderColor: 'rgba(255, 255, 255, 0.6)',
            background: 'rgba(255, 255, 255, 0.7)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 16,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            // Ensure space for end adornments (info icon, units)
            '& input': {
              paddingRight: '40px',
            },
            '& .MuiInputAdornment-positionEnd': {
              marginLeft: 8,
            },
            '& fieldset': {
              borderColor: '#000000',
              borderWidth: 2,
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            },
            '&:hover fieldset': {
              borderColor: '#333333',
              borderWidth: 2,
            },
            '&.Mui-focused': {
              background: 'rgba(255, 255, 255, 1)',
              boxShadow: '0 0 0 4px rgba(255, 255, 255, 0.2), 0 4px 16px rgba(0, 0, 0, 0.03)',
              '& fieldset': {
                borderColor: '#1F1F1F',
                borderWidth: 2,
              },
            },
          },
          '& .MuiInputBase-input': {
            fontSize: '16px',
            color: '#424242',
            '&::placeholder': {
              color: '#bdbdbd',
              opacity: 1,
            },
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 32,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,249,252,0.8) 100%)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 32px 64px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        },
        standardInfo: {
          backgroundColor: 'rgba(240, 245, 255, 0.9)',
          color: '#424242',
        },
        standardSuccess: {
          backgroundColor: 'rgba(240, 255, 240, 0.9)',
          color: '#424242',
        },
        standardWarning: {
          backgroundColor: 'rgba(255, 250, 240, 0.9)',
          color: '#424242',
        },
        standardError: {
          backgroundColor: 'rgba(255, 240, 240, 0.9)',
          color: '#424242',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.03), 0 2px 8px rgba(0, 0, 0, 0.04)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background: 'linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248,249,252,0.9) 100%)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          borderRight: '1px solid rgba(255, 255, 255, 0.2)',
        },
      },
    },
    // (kept Tooltip overrides above with defaultProps)
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          boxShadow: '0 16px 48px rgba(0, 0, 0, 0.1)',
          marginTop: 8,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '4px 8px',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            background: 'rgba(255, 255, 255, 0.6)',
          },
          '&.Mui-selected': {
            background: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              background: 'rgba(255, 255, 255, 0.9)',
            },
          },
        },
      },
    },
    // Global ripple effect styling
    MuiTouchRipple: {
      styleOverrides: {
        root: {
          color: 'var(--current-ripple-color, rgba(0, 0, 0, 0.1))',
        },
        ripple: {
          color: 'var(--current-ripple-color, rgba(0, 0, 0, 0.1))',
          opacity: 1,
        },
        rippleVisible: {
          opacity: 0.15,
          animation: 'mui-ripple-enter 550ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
        child: {
          backgroundColor: 'var(--current-ripple-color, rgba(0, 0, 0, 0.1))',
        },
        childLeaving: {
          backgroundColor: 'var(--current-ripple-color, rgba(0, 0, 0, 0.1))',
          opacity: 0,
          animation: 'mui-ripple-exit 550ms cubic-bezier(0.4, 0, 0.2, 1)',
        },
      },
    },
    // Override ripple for specific components
    MuiButtonBase: {
      styleOverrides: {
        root: {
          // Custom ripple for buttons
          '& .MuiTouchRipple-root': {
            color: 'var(--current-ripple-color, rgba(0, 0, 0, 0.08))',
          },
          '& .MuiTouchRipple-ripple': {
            color: 'var(--current-ripple-color, rgba(0, 0, 0, 0.08))',
          },
        },
      },
    },
    // Override for IconButton
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 44,
          minHeight: 44,
          '&:focus-visible': {
            outline: 'none !important',
            outlineOffset: 0,
            boxShadow: 'none !important',
          },
          '& .MuiTouchRipple-root': {
            color: 'var(--current-ripple-color, rgba(0, 0, 0, 0.1))',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          minHeight: 44,
          '&:focus-visible': {
            outline: 'none !important',
            outlineOffset: 0,
            boxShadow: 'none !important',
          }
        }
      }
    },
    // Override for Chip
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 9999,
          background: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          '& .MuiTouchRipple-root': {
            color: 'var(--current-ripple-color, rgba(0, 0, 0, 0.08))',
          },

          // Ensure readable colors for warning chip (medium reliability)
          '&.MuiChip-filledWarning': {
            backgroundColor: '#FFF4E5', // light amber background
            borderColor: '#FFD8A8',
            color: '#5C3B00', // dark amber text
          },
          '&.MuiChip-filledWarning .MuiChip-label': {
            color: '#5C3B00',
            fontWeight: 600,
          },
          '&.MuiChip-colorWarning': {
            color: '#5C3B00',
          },
        },
      },
    },
    // Add spacing around icons placed as input adornments to avoid crowding with text
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '& .MuiSvgIcon-root': {
            marginLeft: 6,
            marginRight: 6,
          },
        },
      },
    },
  },
});
